<template>
  <div class="candidates">
    <div class="container col-lg-8 col-sm-12">
      <BackBar parent="/talentpools" title="Talent Pools" />
      <div v-if="isFetchingCurrentTalentPool">
        <v-skeleton-loader v-for="i in 10" :key="i" type="list-item-avatar" />
      </div>
      <div v-else>
        <div class="d-flex justify-center mt-4 mb-8">
          <div class="avatar-icon mr-3">
            <v-img height="15px" contain src="@/assets/svg/pool_icon.svg" />
          </div>
          <h1>{{ currentTalentPool.name }}</h1>
        </div>
        <div v-if="isNotEmpty" class="user-list">
          <v-list-item v-for="user in users" :key="user.id">
            <WorkerListItem
              @view="() => handleView(user)"
              @select="() => handleSelect(user)"
              :candidate="user"
              :isSelectMultiple="selectMultiple"
              :isSelected="isSelected(user)"
            />
          </v-list-item>
        </div>
        <EmptyStates v-else description="No candidates yet" />
        <BaseFooter class="relative">
          <MultipleSelectAction
            v-if="!isMobile"
            v-show="selectMultiple"
            :selectedCount="selectedCount"
            :isSelectAll="isSelectAll"
            @selectAll="handleSelectAll"
            @cancel="handleCancel"
          />
        </BaseFooter>
        <BaseFooter>
          <ExpandedButtons>
            <Permissioned
              :vertical="isMobile"
              section="talent-pools"
              class="actions-bar"
            >
              <EditTalentPoolAction
                permission="edit"
                data-test="edit-talent-pool"
              />
              <RemoveTalentPoolAction
                permission="delete"
                data-test="remove-talent-pool"
              />
            </Permissioned>
          </ExpandedButtons>
        </BaseFooter>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import { size, filter, find, isEmpty } from "lodash";
import WorkerListItem from "@/views/workers/components/WorkerListItem";
import EmptyStates from "@/components/common/EmptyStates";
import BaseFooter from "@/components/common/BaseFooter";
import { paginationMixin } from "@/mixins/pagination.mixin";
import MultipleSelectAction from "@/views/workers/components/actions/MultipleSelectAction";
import { TALENT_POOLS_NAMESPACE } from "@/store/modules/talentPools";
import { FETCH_CURRENT_TALENT_POOL } from "@/store/modules/talentPools/actions";
import BackBar from "@/components/common/base-layouts/BackBar";
import Permissioned from "@/components/common/Permissioned";
import ExpandedButtons from "@/components/common/ExpandedButtons";
import EditTalentPoolAction from "@/views/workers/TalentPools/actions/EditTalentPoolAction";
import RemoveTalentPoolAction from "@/views/workers/TalentPools/actions/RemoveTalentPoolAction";

const { mapActions, mapState } = createNamespacedHelpers(
  TALENT_POOLS_NAMESPACE
);

export default {
  mixins: [paginationMixin],
  components: {
    WorkerListItem,
    EmptyStates,
    BaseFooter,
    MultipleSelectAction,
    BackBar,
    Permissioned,
    ExpandedButtons,
    EditTalentPoolAction,
    RemoveTalentPoolAction
  },
  async created() {
    this.fetchCurrentTalentPool(this.currentTalentPoolId);
  },
  data() {
    return {
      selectedUsers: []
    };
  },
  computed: {
    ...mapState({
      currentTalentPool: state => state.currentTalentPool,
      isFetchingCurrentTalentPool: state => state.isFetchingCurrentTalentPool
    }),
    isNotEmpty() {
      return size(this.users);
    },
    selectMultiple() {
      return !isEmpty(this.selectedUsers);
    },
    isSelectAll() {
      return this.isNotEmpty === this.selectedCount;
    },
    selectedCount() {
      return size(this.selectedUsers);
    },
    currentTalentPoolId() {
      return this.$route.params.id;
    },
    users() {
      return this.currentTalentPool ? this.currentTalentPool.users : [];
    }
  },
  methods: {
    ...mapActions({
      fetchCurrentTalentPool: FETCH_CURRENT_TALENT_POOL
    }),
    handleSelect(user) {
      const isSelected = this.isSelected(user);
      isSelected
        ? (this.selectedUsers = filter(
            this.selectedUsers,
            selectedUser => selectedUser.id !== user.id
          ))
        : this.selectedUsers.push(user);
    },
    handleView({ id }) {
      this.$router.push({
        name: "workerTabs",
        params: { id }
      });
    },
    isSelected(user) {
      return Boolean(
        find(this.selectedUsers, selectedUser => selectedUser.id === user.id)
      );
    },
    handleSelectAll() {
      this.selectedUsers = this.users;
    },
    handleCancel() {
      this.selectedUsers = [];
    }
  }
};
</script>

<style lang="scss"></style>
